// Hooks
import { useReducer, createRef, useEffect } from 'react';
import { useRouter } from 'next/router';

// Analytics
import { analyticsClient } from '../../client/analytics';

// Components
import CarouselItem from './CarouselItem';

// Case state reducer
const carouselReducer = (state, action) => {
  const newState = { ...state }; // eslint-disable-line no-unused-vars

  switch (action.type) {
    case 'UPDATE_SLIDE':
      if (action.index === state.active) {
        return state;
      }
      return { ...state, previous: newState.active, active: action.index };
    case 'PREVIOUS_SLIDE':
      return { ...state, previous: newState.active, active: action.index < 0 ? state.total - 1 : action.index };
    case 'NEXT_SLIDE':
      return { ...state, previous: newState.active, active: action.index === state.total ? 0 : action.index };
    default:
      return state;
  }
};

const Carousel = (props) => {
  const { title, defaultLiftNumberLabel, slides, hideTitle, modifier, hideNav } = props;

  const router = useRouter();

  // DOM references for carousel slides and its labels
  const refCarouselItems = [];
  const refLabelItems = [];

  // Map slide items and labels elements
  const labelsElements = [];
  const slideElements = [];

  // Create array with number to store in state
  const liftNumberLabels = [];

  // determine what the scale transform origin of the nav underline should be based on previous selected item
  const onSelectNavItem = (index) => {
    for (let i = 0; i < refLabelItems.length; i += 1) {
      refLabelItems[i].current.classList.remove('is-scalefromleft');
      refLabelItems[i].current.classList.remove('is-scalefromright');

      if (i === index) {
        // eslint-disable-next-line no-use-before-define
        if (carouselState.previous > index) {
          refLabelItems[i].current.classList.add('is-scalefromright');
        } else {
          refLabelItems[i].current.classList.add('is-scalefromleft');
        }
        refLabelItems[i].current.classList.add('is-active');
      } else {
        if (index < i) {
          refLabelItems[i].current.classList.add('is-scalefromleft');
        } else {
          refLabelItems[i].current.classList.add('is-scalefromright');
        }
        refLabelItems[i].current.classList.remove('is-active');
      }
    }
  };

  // Map slides and labels
  slides.forEach((item, index) => {
    const {
      fields: { label, liftNumberLabel },
      sys: { id },
    } = item;

    // crate DOM references
    refLabelItems[index] = createRef();
    refCarouselItems[index] = createRef();

    const handleClick = () => {
      // eslint-disable-next-line no-use-before-define
      dispatch({ type: 'UPDATE_SLIDE', index });
      analyticsClient.track('[Carousel] Slide label clicked', {
        title,
        label,
      });

      onSelectNavItem(index);
    };

    labelsElements.push(
      <span
        key={`Carousel-slideLabel-${id}`}
        ref={refLabelItems[index]}
        className="Carousel-slideLabel"
        onClick={handleClick}
      >
        {label}
      </span>,
    );

    slideElements.push(<CarouselItem key={`CarouselItem-${id}`} {...item} ref={refCarouselItems[index]} />);

    liftNumberLabels.push(liftNumberLabel || defaultLiftNumberLabel);
  });

  const pdfCarouselSlides = [];

  slides.forEach((item) => {
    const {
      sys: { id },
    } = item;

    pdfCarouselSlides.push(
      <div key={`CarouselItem--pdf-${id}`} className="CarouselItem--pdf">
        <div className="Carousel-slideLabel">{item.fields.label}</div>
        <img src={item.fields.largeMedia.fields.file.url} alt={item.fields.label} />
      </div>,
    );
  });

  // Carousel state
  const [carouselState, dispatch] = useReducer(carouselReducer, {
    previous: null,
    total: slideElements.length,
    active: 0,
    labels: liftNumberLabels,
  });

  // current lift number label
  const liftNumber = carouselState.labels[carouselState.active];

  // Animate slides
  useEffect(() => {
    const { previous, active } = carouselState;

    // Check if there is an previous carousel item
    if (refCarouselItems[previous]) {
      setTimeout(() => {
        refCarouselItems[previous].current.classList.remove('is-active');
      }, 100);
    }

    refCarouselItems[active].current.classList.add('is-active');

    onSelectNavItem(active);
  }, [carouselState.previous, carouselState.active]);

  const handleNext = () => {
    dispatch({ type: 'NEXT_SLIDE', index: carouselState.active + 1 });
    analyticsClient.track('[Carousel] Next arrow clicked', {
      title,
      currentIndex: carouselState.active,
      nextIndex: carouselState.active + 1,
    });
  };
  const handlePrev = () => {
    dispatch({ type: 'PREVIOUS_SLIDE', index: carouselState.active - 1 });
    analyticsClient.track('[Carousel] Previous arrow clicked', {
      title,
      currentIndex: carouselState.active,
      nextIndex: carouselState.active - 1,
    });
  };

  // @todo adjust mobile navigation

  return (
    <>
      <div className={`Carousel ${modifier || ''}`}>
        <div className="Carousel-inner">
          {!hideTitle && (
            <div className="Carousel-head">
              <div className="Carousel-title">{title}</div>
            </div>
          )}
          <div className="Carousel-container">
            <div className="Carousel-navigation" style={hideNav && { display: 'none' }}>
              <div className="Carousel-labels">{labelsElements}</div>
              {liftNumber && <div className="Carousel-liftNumber">{liftNumber}</div>}
            </div>
            <div className="Carousel-slides">
              {carouselState.total > 1 && (
                <>
                  <div className="Carousel-arrow Carousel--previous" onClick={handlePrev}>
                    <svg>
                      <use xlinkHref="#carousel-arrow-previous" />
                    </svg>
                  </div>
                  <div className="Carousel-arrow  Carousel--next" onClick={handleNext}>
                    <svg>
                      <use xlinkHref="#carousel-arrow-next" />
                    </svg>
                  </div>
                </>
              )}

              {slideElements}
            </div>
          </div>
        </div>
      </div>
      {typeof window !== 'undefined' && router.query.download === 'pdf' && pdfCarouselSlides}
    </>
  );
};

export default Carousel;
